import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { LockAuditLog, LogsListTypes } from './types';
import useQueryState from '../table/hooks/use-query-state';
import usePrevious from '../../hooks/use-previous';
import useGetList from '../../hooks/use-get-list';
import useFilters from '../table/hooks/use-filters';
import formatLogsList from './helpers/format-logs-list';
import { actions } from './variables/lists';
import { Filters, SchemaFilterType } from '../table/types';
import { PermissionEnum } from '../../base-types';
import PermissionDenied from '../icons/permission-denied';
import IconLoader from '../icons/loader';
import DateInputRange from '../date-input/date-input-range';
import Table from '../table/table';
import InputText from '../inputs/input-text';

import cn from './styles.module.css';

let timeout: NodeJS.Timeout;

export default function SecurityLogs({
  logs,
  pagination,
  takeCount,
  activePermissions,
  schema,
  getList,
  userId,
  isLoading,
  filtersSchema,
  columns,
  columnsLabels,
  isFetching,
  tableTopClassName = '',
}: LogsListTypes) {
  const [createdAt, setCreatedAt] = useState<string[]>([]);
  const [timeframeMinutes, setTimeframeMinutes] = useState<string>('');

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const { search } = useLocation();

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<LockAuditLog>({
      getList,
      onFilterChange,
      onSortChange,
      schema,
    });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatLogsList(logs as LockAuditLog[]);

    return data;
  }, [logs, sort, filters]);

  useEffect(() => {
    setValues('action', actions);
  }, []);

  useEffect(() => {
    if (takeCount && takeCount !== 0 && filters && !logs && search) {
      const nextFilters: Filters = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      if (userId) {
        nextFilters.userId = {
          value: [userId],
          type: SchemaFilterType.STRING,
        };
      }

      if (search) {
        const parsed = queryString.parse(search);

        if (parsed.eventCreatedAtFrom) {
          const createdAtDates: string[] = [
            new Date(parsed.eventCreatedAtFrom as string).toISOString(),
          ];

          if (parsed.eventCreatedAtTo) {
            createdAtDates.push(
              new Date(parsed.eventCreatedAtTo as string).toISOString(),
            );
          }

          setCreatedAt(createdAtDates);

          nextFilters.eventCreatedAt = {
            value: createdAtDates,
            type: SchemaFilterType.DATE_RANGE,
          };
        }

        if (parsed.timeframeMinutes) {
          setTimeframeMinutes(parsed.timeframeMinutes as string);

          nextFilters.timeframeMinutes = {
            value: [parsed.timeframeMinutes as string],
            type: SchemaFilterType.STRING,
          };
        }
      }

      getListAction({
        filters: nextFilters,
      });
    }
  }, [takeCount, search, logs, filters, userId]);

  useEffect(() => {
    if (
      takeCount &&
      takeCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !logs &&
      !search
    ) {
      const nextFilters: Filters = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      if (userId) {
        nextFilters.userId = {
          value: [userId],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({
        filters: nextFilters,
      });
    }
  }, [filters, prevFilters, logs, takeCount, search, userId]);

  let content;

  if (!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)) {
    content = <PermissionDenied />;
  } else if (activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)) {
    if (isLoading) {
      content = (
        <div className={cn.locksLoader}>
          <IconLoader />
        </div>
      );
    } else {
      content = (
        <div className={cn.securityLocksTableWrapper}>
          <Table
            columns={columns}
            data={formattedData || []}
            onSort={() => {}}
            filters={filters}
            sort={sort}
            columnsLabels={columnsLabels}
            tableTopClassName={`${cn.locksTableTop} ${tableTopClassName}`}
            onFilterChange={(filterValues) => {
              const nextFilters: Filters = {
                ...filterValues,
                take: {
                  value: [(takeCount as number)?.toString()],
                  type: SchemaFilterType.STRING,
                },
              };

              if (userId) {
                nextFilters.userId = {
                  value: [userId],
                  type: SchemaFilterType.STRING,
                };
              }

              if (createdAt?.length) {
                const createdAtDates: string[] = [
                  new Date(createdAt[0]).toISOString(),
                ];

                if (createdAt?.length > 1) {
                  createdAtDates.push(new Date(createdAt[1]).toISOString());
                }

                nextFilters.eventCreatedAt = {
                  value: createdAtDates,
                  type: SchemaFilterType.DATE_RANGE,
                };
              }

              if (timeframeMinutes) {
                nextFilters.timeframeMinutes = {
                  value: [timeframeMinutes],
                  type: SchemaFilterType.STRING,
                };
              }

              onFilter({
                ...nextFilters,
              });
            }}
            topContent={
              <div className={cn.securityDatesControls}>
                <DateInputRange
                  values={createdAt}
                  placeholder="Select Creation dates"
                  dateInputTitle="Creation Date"
                  className={`${cn.securityDatesPicker} ${
                    !createdAt.length ? cn.empty : ''
                  }`}
                  onChangeRange={(dates) => {
                    setCreatedAt(
                      dates?.map((item) => new Date(item).toISOString()),
                    );

                    const nextFilters: Filters = {
                      ...filters,
                      take: {
                        value: [(takeCount as number)?.toString()],
                        type: SchemaFilterType.STRING,
                      },
                    };

                    if (userId) {
                      nextFilters.userId = {
                        value: [userId],
                        type: SchemaFilterType.STRING,
                      };
                    }

                    const datesValues: string[] = [
                      new Date(dates[0]).toISOString(),
                    ];

                    if (dates?.length > 1) {
                      datesValues.push(new Date(dates[1]).toISOString());
                    }

                    nextFilters.eventCreatedAt = {
                      value: datesValues,
                      type: SchemaFilterType.DATE_RANGE,
                    };

                    onFilter({
                      ...nextFilters,
                    });
                  }}
                  onReset={() => {
                    setCreatedAt([]);

                    const nextFilters: Filters = {
                      ...filters,
                      take: {
                        value: [(takeCount as number)?.toString()],
                        type: SchemaFilterType.STRING,
                      },
                    };

                    if (userId) {
                      nextFilters.userId = {
                        value: [userId],
                        type: SchemaFilterType.STRING,
                      };
                    }

                    if (nextFilters.eventCreatedAt) {
                      delete nextFilters.eventCreatedAt;
                    }

                    onFilter({
                      ...nextFilters,
                    });
                  }}
                  withResetClose
                />
                <InputText
                  name="timeframeMinutes"
                  title="Interval"
                  placeholder="Enter interval (min)"
                  labelClassName={cn.intervalInput}
                  value={timeframeMinutes}
                  onChange={(e) => {
                    clearTimeout(timeout);

                    setTimeframeMinutes(e.target.value);

                    timeout = setTimeout(() => {
                      const nextFilters: Filters = {
                        ...filters,
                        take: {
                          value: [(takeCount as number)?.toString()],
                          type: SchemaFilterType.STRING,
                        },
                      };

                      if (userId) {
                        nextFilters.userId = {
                          value: [userId],
                          type: SchemaFilterType.STRING,
                        };
                      }

                      if (e.target.value) {
                        nextFilters.timeframeMinutes = {
                          value: [e.target.value],
                          type: SchemaFilterType.STRING,
                        };
                      } else {
                        delete nextFilters.timeframeMinutes;
                      }

                      onFilter({
                        ...nextFilters,
                      });
                    }, 300);
                  }}
                />
              </div>
            }
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isLoading || isFetching || !takeCount}
            take="30"
            setPage={(data) => {
              const filterValues: Filters = {
                ...filters,
                take: {
                  value: [(takeCount as number)?.toString()],
                  type: SchemaFilterType.STRING,
                },
              };

              if (userId) {
                filterValues.userId = {
                  value: [userId],
                  type: SchemaFilterType.STRING,
                };
              }

              if (createdAt?.length) {
                const createdAtDates: string[] = [
                  new Date(createdAt[0]).toISOString(),
                ];

                if (createdAt?.length > 1) {
                  createdAtDates.push(new Date(createdAt[1]).toISOString());
                }

                filterValues.eventCreatedAt = {
                  value: createdAtDates,
                  type: SchemaFilterType.DATE_RANGE,
                };
              }

              if (timeframeMinutes) {
                filterValues.timeframeMinutes = {
                  value: [timeframeMinutes],
                  type: SchemaFilterType.STRING,
                };
              }

              setPage({ ...data, filters: filterValues });
            }}
            pagination={pagination}
            error={errorList}
            withScroll
            clickable
          />
        </div>
      );
    }
  }

  return content;
}
